import React, { useState } from 'react';
import { PayPalButtons, PayPalScriptProvider } from '@paypal/react-paypal-js';
import { useTranslation } from 'react-i18next';
import './PayPalPayment.css';

interface PayPalPaymentProps {
    amount: number;
    project: string;
    donationFrequency: string;
    onSuccess: (paymentData: any) => void;
    onFailure: (error: any) => void;
}

const PayPalPayment: React.FC<PayPalPaymentProps> = ({
    amount,
    project,
    donationFrequency,
    onSuccess,
    onFailure
}) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);

    // Convert INR to USD (approximate rate: 1 USD = 83 INR)
    const amountUSD = (amount / 83).toFixed(2);

    const initialOptions = {
        clientId: process.env.REACT_APP_PAYPAL_CLIENT_ID || '',
        currency: 'USD',
        intent: 'capture'
    };

    return (
        <div className="paypal-payment">
            <div className="payment-info">
                <h3 className="payment-title">{t('donationForm.paypal')}</h3>
                <p className="payment-description">
                    {t('donationForm.paypalDescription')}
                </p>
                <div className="payment-details">
                    <div className="detail-row">
                        <span className="detail-label">{t('donationForm.amountINR')}:</span>
                        <span className="detail-value">₹{amount.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">{t('donationForm.amountUSD')}:</span>
                        <span className="detail-value">${amountUSD}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">{t('donationForm.projectLabel')}:</span>
                        <span className="detail-value">{project}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-label">{t('donationForm.frequencyLabel')}:</span>
                        <span className="detail-value">{donationFrequency}</span>
                    </div>
                </div>
            </div>

            <div className="paypal-buttons-container">
                {initialOptions.clientId ? (
                    <PayPalScriptProvider options={initialOptions}>
                        <PayPalButtons
                            style={{
                                layout: 'vertical',
                                color: 'gold',
                                shape: 'rect',
                                label: 'donate'
                            }}
                            createOrder={(data, actions) => {
                                return actions.order.create({
                                    intent: 'CAPTURE',
                                    purchase_units: [
                                        {
                                            amount: {
                                                value: amountUSD,
                                                currency_code: 'USD'
                                            },
                                            description: `Donation for ${project}`,
                                            custom_id: `${project}-${donationFrequency}`
                                        }
                                    ]
                                });
                            }}
                            onApprove={async (data, actions) => {
                                setLoading(true);
                                try {
                                    const details = await actions.order!.capture();
                                    onSuccess({
                                        paypal_order_id: data.orderID,
                                        paypal_payer_id: data.payerID,
                                        payment_details: details,
                                        amount,
                                        amountUSD: parseFloat(amountUSD),
                                        project,
                                        donationFrequency,
                                        paymentMethod: 'paypal',
                                        paymentStatus: 'success'
                                    });
                                    setLoading(false);
                                } catch (error) {
                                    setLoading(false);
                                    onFailure(error);
                                }
                            }}
                            onError={(err) => {
                                setLoading(false);
                                onFailure(err);
                            }}
                            onCancel={() => {
                                setLoading(false);
                                onFailure({ message: t('donationForm.paymentCancelled') });
                            }}
                        />
                    </PayPalScriptProvider>
                ) : (
                    <div className="error-message">
                        PayPal Client ID is not configured. Please add REACT_APP_PAYPAL_CLIENT_ID to your .env file.
                    </div>
                )}
            </div>

            {loading && (
                <div className="paypal-loading">
                    <div className="spinner"></div>
                    <span>Processing payment...</span>
                </div>
            )}

            <div className="secure-payment-badge">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 2L4 6V12C4 16.4 7.6 20.4 12 22C16.4 20.4 20 16.4 20 12V6L12 2Z" fill="#4caf50" />
                    <path d="M10 14L7 11L8.4 9.6L10 11.2L15.6 5.6L17 7L10 14Z" fill="white" />
                </svg>
                <span>{t('donationForm.securePayment')}</span>
            </div>
        </div>
    );
};

export default PayPalPayment;
